import { useEffect, useRef } from 'react';
import { useAppDispatch } from '../../store';
import { setPaymentProduct } from '../../reduxStore/data/actions';
import { resolveTariffPayment } from '../tariffPaymentConfig';
import { getParameterFromURL } from '../../utils/scripts';
import { getPaymentCurrency } from '../../utils/scripts/currency';
import { clearPaypalRestoreProductId, getPaypalRestoreProductId } from '../../utils/payments/paypalRestoreProduct';

/**
 * Keeps the payment product in sync with the current tariff.
 *
 * All tariff → product mappings live in tariffPaymentConfig.ts — adding a new
 * tariff means adding one entry there, not editing the paywall.
 *
 * PayPal redirect short-circuit: when returning from a PayPal flow the product
 * is already restored from session storage and must not be overwritten. On
 * initial mount an existing product is also left alone (set by another paywall
 * or restore path); subsequent tariff changes always re-dispatch. Once the
 * restored product lands in the store, the session-storage marker is cleared.
 */
export function usePaymentProductSync(tariff: string, productName: string | null): void {
  const dispatch = useAppDispatch();

  const paypalRestoreProductId = getPaypalRestoreProductId();
  const hasPaypalRedirectProduct = Boolean(getParameterFromURL('paypal_product_id') || paypalRestoreProductId);

  const isInitialMountRef = useRef(true);
  useEffect(() => {
    const isInitialMount = isInitialMountRef.current;
    isInitialMountRef.current = false;

    if (hasPaypalRedirectProduct) return;
    if (isInitialMount && productName) return;

    const subscrUK = getParameterFromURL('subscrUK') === 'true';
    const payment = resolveTariffPayment(tariff, { currency: getPaymentCurrency(), subscrUK });
    if (!payment) return;

    dispatch(
      setPaymentProduct(
        JSON.stringify({
          product: payment.productId,
          oldPrice: payment.oldPrice,
          name: payment.productName,
        })
      )
    );
  }, [tariff]);

  useEffect(() => {
    if (!paypalRestoreProductId || !productName) {
      return;
    }

    if (String(productName) === paypalRestoreProductId) {
      clearPaypalRestoreProductId();
    }
  }, [paypalRestoreProductId, productName]);
}
